import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Sparkles, Image as ImageIcon, FileText, Upload, X, Check, Video, Trash2, ChevronDown, Megaphone, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { DEFAULT_CAMPAIGN_CHANNEL } from '@/lib/campaignChannel';
import { campaignFromTemplatePath, mediaHandOff, templateFromMediaPath } from '@/lib/flowLinks';
import { formatTemplateName } from '@/lib/templateName';
import { useWorkspace, usePermissions, useFeature } from '@/contexts/WorkspaceContext';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useMediaErrorRefresh } from '@/hooks/useMediaErrorRefresh';
import AssetDownloadButton from '@/components/AssetDownloadButton';
import GenerationLoader from '@/components/GenerationLoader';
import {
  GeneratedContentV2,
  normalize,
  generationCount,
  selectedText,
  selectedPhotos,
  selectedVideos,
  setTextSelection,
  togglePhoto,
  toggleVideo,
  removeGeneration,
  withFreshUrls,
} from '@/lib/generatedContent';
import { calculateGenerationCost, costBreakdown, formatCredits } from '@/lib/creditCosts';

interface ReferenceImage {
  data: string;
  mimeType: string;
  preview: string;
}

// Sent to n8n as `status` ("new campaign" / "retargeting campaign"), mapped server-side.
type CampaignStage = 'new' | 'retargeting';

const CAMPAIGN_STAGE_OPTIONS: { value: CampaignStage; label: string }[] = [
  { value: 'new', label: 'New campaign' },
  { value: 'retargeting', label: 'Retargeting campaign' },
];

// A WhatsApp template made from this media, as GET /whatsapp-templates?campaignMediaId= lists it.
interface LinkedTemplate {
  id: string;
  name: string;
  status: string;
  rejectionReason: string | null;
}

export default function MediaBuilder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId, workspace, isSuperAdmin, creditCosts, refreshWorkspace } = useWorkspace();
  const { guard, nudge } = useSubscriptionGuard('Creating campaigns requires an active subscription.');
  const showError = useApiErrorToast();
  const { can } = usePermissions();
  const hasCustomTemplates = useFeature('templates_custom');
  // Where this creative goes next: into a WhatsApp template for anyone who can make one, else
  // straight into a campaign — see lib/flowLinks.
  const handOff = mediaHandOff({
    canManageTemplates: can('templates:manage'),
    hasCustomTemplates,
    canCreateCampaign: can('campaign:create'),
  });
  const canReadTemplates = can('templates:read');
  const canCreateCampaign = can('campaign:create');
  // One hand-off at a time: a second click used to create a second campaign.
  const [starting, setStarting] = useState(false);
  const [linkedTemplates, setLinkedTemplates] = useState<LinkedTemplate[]>([]);
  // "new" is the create-mode sentinel in the URL, not an id. Without normalising it here the
  // hydrate effect below fires GET /campaign-media/new, which errors against a UUID column and
  // pops a spurious "Failed to load this media." toast before falling through to the blank form.
  const { mediaId: mediaIdParam } = useParams<{ mediaId: string }>();
  const editId = mediaIdParam === 'new' ? undefined : mediaIdParam;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hydrating, setHydrating] = useState<boolean>(!!editId);
  const [formOpen, setFormOpen] = useState(true);

  // Form State
  const [name, setName] = useState('');
  const [platforms, setPlatforms] = useState<string[]>(['ad']);
  const [campaignStage, setCampaignStage] = useState<CampaignStage>('new');
  const [unitType, setUnitType] = useState('');
  const [propertyRange, setPropertyRange] = useState('');
  const [pricing, setPricing] = useState('');
  const [location, setLocation] = useState('');
  const [developer, setDeveloper] = useState('');
  const [brief, setBrief] = useState('');
  const [contentTypes, setContentTypes] = useState<string[]>(['text']);
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);

  // Generated Content State (v2)
  const [content, setContent] = useState<GeneratedContentV2 | null>(null);
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [showGenConfirm, setShowGenConfirm] = useState(false);

  // WhatsApp only changes which prompt n8n runs — the review flow below is the
  // same for every type. Picking a message template is a launch-time concern and
  // lives on the campaign page, beside the audience and the preview.
  const isWhatsApp = platforms.includes('whatsapp');
  // Use first platform for the media type (the generator needs a single type)
  const primaryPlatform = platforms[0] || 'ad';

  // ---- Resume/edit mode: hydrate from an existing campaign ----
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      setHydrating(true);
      try {
        const { media } = await apiFetch<{ media: Record<string, unknown> }>(`/workspaces/${workspaceId}/campaign-media/${editId}`);
        if (cancelled) return;
        setMediaId(media.id as string);
        setName((media.name as string) || '');
        setCampaignStage(media.campaignStage === 'retargeting' ? 'retargeting' : 'new');
        setUnitType((media.unitType as string) || '');
        setLocation((media.location as string) || '');
        setDeveloper((media.developer as string) || '');
        setPricing((media.pricing as string) || '');
        setPropertyRange((media.propertyRange as string) || '');
        setBrief((media.brief as string) || '');
        const ct = media.contentTypes as string[] | undefined;
        setContentTypes(ct?.length ? ct : ['text']);
        const meta = media.metadata as { platforms?: string[] } | undefined;
        const plats = meta?.platforms?.length ? meta.platforms : [(media.type as string) || 'ad'];
        setPlatforms(plats);
        const gc = normalize(media.generatedContent);
        setContent(gc.generations.length ? gc : null);
        setFormOpen(!gc.generations.length); // collapse the form when there's already content
      } catch (error) {
        if (!cancelled) showError(error, 'Failed to load this media.');
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, workspaceId, showError]);

  // A photo or video that fails to load here has almost always outlived its signed URL: the page has
  // been open for about a day. Re-read the media for fresh URLs and swap in only those, so a selection
  // being made on screen is not thrown away.
  const refreshMediaUrls = useMediaErrorRefresh(async () => {
    if (!mediaId) return;
    try {
      const { media } = await apiFetch<{ media: { generatedContent?: unknown } }>(
        `/workspaces/${workspaceId}/campaign-media/${mediaId}`,
      );
      const fresh = normalize(media.generatedContent);
      setContent((current) => (current ? withFreshUrls(current, fresh) : current));
    } catch (error) {
      console.warn('[MediaBuilder] could not refresh media URLs:', error);
    }
  });

  // The templates made from this media, so the page that started the flow also shows where it got
  // to: one in review, one approved and ready to start a campaign. A read of the local list — its
  // statuses are as fresh as the last sync, which the Templates page runs whenever it opens.
  useEffect(() => {
    if (!mediaId || !canReadTemplates) {
      setLinkedTemplates([]);
      return;
    }
    let cancelled = false;
    apiFetch<{ templates: LinkedTemplate[] }>(
      `/workspaces/${workspaceId}/whatsapp-templates?campaignMediaId=${mediaId}&limit=20`,
    )
      .then((data) => { if (!cancelled) setLinkedTemplates(data.templates || []); })
      .catch((error) => {
        // Context, not content: the builder works the same without it.
        console.warn('[Media Builder] could not load the templates made from this media:', error);
        if (!cancelled) setLinkedTemplates([]);
      });
    return () => { cancelled = true; };
  }, [mediaId, workspaceId, canReadTemplates]);

  const handlePlatformToggle = (value: string, checked: boolean) => {
    if (checked) {
      setPlatforms(prev => [...prev, value]);
    } else {
      setPlatforms(prev => prev.filter(p => p !== value));
    }
  };

  const handleContentTypeChange = (type: string, checked: boolean) => {
    if (checked) {
      setContentTypes([...contentTypes, type]);
    } else {
      setContentTypes(contentTypes.filter(t => t !== type));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const maxImages = 5;
    const maxSize = 4 * 1024 * 1024; // 4MB per image

    if (referenceImages.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    const newImages: ReferenceImage[] = [];

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }

      if (file.size > maxSize) {
        toast.error(`${file.name} is too large (max 4MB)`);
        continue;
      }

      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data:image/xxx;base64, prefix
            const base64Data = result.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newImages.push({
          data: base64,
          mimeType: file.type as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif',
          preview: URL.createObjectURL(file)
        });
      } catch {
        toast.error(`Failed to process ${file.name}`);
      }
    }

    setReferenceImages([...referenceImages, ...newImages]);
    e.target.value = ''; // Reset input
  };

  const removeImage = (index: number) => {
    const newImages = [...referenceImages];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setReferenceImages(newImages);
  };

  // Create the media row once, or persist edited form fields on reuse.
  const ensureMedia = async (): Promise<string> => {
    if (mediaId) {
      await apiFetch(`/workspaces/${workspaceId}/campaign-media/${mediaId}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, campaignStage, brief, pricing, propertyRange, unitType, location, developer }),
      });
      return mediaId;
    }
    const data = await apiFetch<{ media: { id: string } }>(`/workspaces/${workspaceId}/campaign-media`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        type: isWhatsApp ? 'whatsapp' : primaryPlatform,
        campaignStage,
        pricing,
        propertyRange,
        unitType,
        location,
        brief,
        developer,
        contentTypes,
        metadata: { platforms },
        referenceImages: referenceImages.map(img => ({ data: img.data, mimeType: img.mimeType })),
      }),
    });
    setMediaId(data.media.id);
    return data.media.id;
  };

  // Validate then open the credit-cost confirm dialog.
  const requestGenerate = () => {
    if (platforms.length === 0) {
      toast.error('Please select at least one campaign channel');
      return;
    }
    if (!name || !unitType || !location) {
      toast.error('Please fill in the required fields (Campaign Name, Unit Type, Location)');
      return;
    }
    if (contentTypes.length === 0) {
      toast.error('Please select at least one content type');
      return;
    }
    setShowGenConfirm(true);
  };

  const runGeneration = async () => {
    // One generation at a time from this page. The server reserves credits per request, so a second
    // click would be charged as a second generation.
    if (loading) return;
    setShowGenConfirm(false);
    setLoading(true);
    setFormOpen(false); // focus the loader while generating
    try {
      const cid = await ensureMedia();

      // Persist the current selection first, so the server-side append preserves
      // it while adding the new option.
      if (content) {
        try {
          await apiFetch(`/workspaces/${workspaceId}/campaign-media/${cid}`, {
            method: 'PATCH',
            body: JSON.stringify({ generatedContent: content }),
          });
        } catch (error) {
          // Best-effort: the generation below is what matters, and it re-sends the selection.
          console.warn('[Media Builder] could not persist selection before generating:', error);
        }
      }

      const resultData = await apiFetch<{ media: { generatedContent: unknown } }>(
        `/workspaces/${workspaceId}/workflows/campaign-generation/trigger`,
        {
          method: 'POST',
          body: JSON.stringify({ campaignMediaId: cid, contentTypes }),
        },
      );

      setContent(normalize(resultData.media.generatedContent));
      setFormOpen(false); // collapse the form so the review takes the full width
      toast.success('Campaign content generated successfully!');
    } catch (error) {
      console.error('[Media Builder] generation failed:', error);
      // The server marks its 502 `expose`, so the AI provider's own reason — a quota, a timeout, a
      // rejected prompt — survives production's 5xx erasure and lands here.
      showError(error, 'Failed to generate content. Please try again.');
    } finally {
      setLoading(false);
      // Either way the balance moved: charged for what was produced, refunded for what was not.
      refreshWorkspace();
    }
  };

  // Persist a selection change (setContent + PATCH generatedContent).
  const persistContent = async (next: GeneratedContentV2) => {
    setContent(next);
    if (!mediaId) return;
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaign-media/${mediaId}`, {
        method: 'PATCH',
        body: JSON.stringify({ generatedContent: next }),
      });
    } catch (error) {
      // The selection is still held in state, so this is not worth interrupting anyone over.
      console.warn('[Media Builder] could not persist selection:', error);
    }
  };

  const handleSelectText = (uid: string) => { if (content) persistContent(setTextSelection(content, uid)); };
  const handleTogglePhoto = (uid: string) => { if (content) persistContent(togglePhoto(content, uid)); };
  const handleToggleVideo = (uid: string) => { if (content) persistContent(toggleVideo(content, uid)); };
  const handleDiscard = (genId: string) => { if (content) persistContent(removeGeneration(content, genId)); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cid = await ensureMedia();
      if (content) {
        await apiFetch(`/workspaces/${workspaceId}/campaign-media/${cid}`, {
          method: 'PATCH',
          body: JSON.stringify({ generatedContent: content }),
        });
      }
      toast.success('Media saved');
      navigate(`/w/${workspaceId}/campaigns/media`);
    } catch (error) {
      showError(error, 'Failed to save this media.');
    } finally {
      setSaving(false);
    }
  };

  // Start a campaign from this creative.
  //
  // This replaces a button that used to flip the row's status to 'live' and navigate away without
  // sending anything at all. Draft-first makes that impossible to keep: a campaign is now a real
  // row with its own page, and launching happens there, next to the audience and the preview of
  // what will actually go out.
  //
  // Now the fallback for anyone who cannot make a template (see lib/flowLinks): they still need a
  // way forward from their media, and the campaign still picks from the templates that exist.
  const handleUseInCampaign = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const cid = await ensureMedia();
      if (content) {
        await apiFetch(`/workspaces/${workspaceId}/campaign-media/${cid}`, {
          method: 'PATCH',
          body: JSON.stringify({ generatedContent: content }),
        });
      }
      const { campaign } = await apiFetch<{ campaign: { id: string } }>(
        `/workspaces/${workspaceId}/campaigns`,
        // A media item belongs to no channel, so the campaign takes the one that ships.
        { method: 'POST', body: JSON.stringify({ name, campaignMediaId: cid, type: DEFAULT_CAMPAIGN_CHANNEL }) },
      );
      // Step 1 of the setup stepper: the name is prefilled from the media and the media is already
      // chosen, so this is a confirmation, not a form to fill in.
      navigate(`/w/${workspaceId}/campaigns/${campaign.id}?step=details`);
    } catch (error) {
      showError(error, 'Could not start a campaign from this media.');
    } finally {
      setStarting(false);
    }
  };

  // Make a WhatsApp template from this creative — the main road forward.
  //
  // For WhatsApp, the generated text has nowhere else to go: a campaign sends the approved
  // template, whose body is fixed when it is created. So the composer opens with this media's text
  // as the body and its selected photo or video as the header, every field still editable, and the
  // template remembers this media so campaigns using it start with the same creative.
  const handleUseInTemplate = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const cid = await ensureMedia();
      // The current selection is what the composer reads, so it is saved first.
      if (content) {
        await apiFetch(`/workspaces/${workspaceId}/campaign-media/${cid}`, {
          method: 'PATCH',
          body: JSON.stringify({ generatedContent: content }),
        });
      }
      navigate(templateFromMediaPath(workspaceId, cid));
    } catch (error) {
      showError(error, 'Could not open this media as a template.');
    } finally {
      setStarting(false);
    }
  };

  const genCount = generationCount(content);
  // Priced from the backend's own table (useWorkspace().creditCosts), so the quote is the charge.
  const totalCost = calculateGenerationCost(contentTypes, creditCosts.generation);
  const cannotAfford = !isSuperAdmin && totalCost > (workspace.creditsRemaining || 0);
  const selText = content ? selectedText(content) : null;
  const selPhotos = content ? selectedPhotos(content) : [];
  const selVideos = content ? selectedVideos(content) : [];
  // A template is pre-filled from the final selection, so with nothing selected there is nothing
  // to carry across.
  const hasSelection = Boolean(selText) || selPhotos.length > 0 || selVideos.length > 0;
  const summaryLine = [
    name || 'Untitled campaign',
    isWhatsApp ? 'WhatsApp' : (platforms.join(', ') || 'no channel'),
    campaignStage === 'retargeting' ? 'Retargeting' : 'New campaign',
    contentTypes.join(', '),
  ].filter(Boolean).join(' · ');

  // While loading an existing campaign for resume/preview.
  if (hydrating) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading campaign…
        </main>
      </div>
    );
  }

  // Reusable full-width option card for the compare/mix review. Text spans the
  // full width on its own row; photo(s) and video(s) sit in a media row below —
  // each takes half when both are present, and full width when the option has
  // only one media type.
  const renderOptionCard = (gen: GeneratedContentV2['generations'][number]) => {
    const hasPhotos = gen.photoOptions.length > 0;
    const hasVideos = gen.videoOptions.length > 0;
    const bothMedia = hasPhotos && hasVideos;
    return (
      <div key={gen.id} className="rounded-lg border-2 border-muted p-5 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">{gen.label}</span>
          {content && content.generations.length > 1 && (
            <button
              type="button"
              title="Discard this option"
              onClick={() => handleDiscard(gen.id)}
              className="text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        {gen.inputs?.brief ? (
          <p className="text-xs text-muted-foreground line-clamp-2">Brief: {String(gen.inputs.brief)}</p>
        ) : null}

        {/* Text row (full width) */}
        {gen.textOptions.map((o) => {
          const isSel = content?.selection.textUid === o.uid;
          return (
            <div
              key={o.uid}
              onClick={() => handleSelectText(o.uid)}
              className={`cursor-pointer rounded-md border p-4 transition-all ${isSel ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium flex items-center gap-1"><FileText className="h-3 w-3" /> Text</span>
                {isSel && <span className="text-xs text-primary flex items-center gap-1"><Check className="h-3 w-3" /> In use</span>}
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{o.content}</p>
            </div>
          );
        })}

        {/* Media row: photos + videos */}
        {(hasPhotos || hasVideos) && (
          <div className={`grid gap-4 ${bothMedia ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
            {hasPhotos && (
              <div className="space-y-3">
                {gen.photoOptions.map((o, i) => {
                  const isSel = content?.selection.photoUids.includes(o.uid);
                  return (
                    <div
                      key={o.uid}
                      onClick={() => handleTogglePhoto(o.uid)}
                      className={`group relative cursor-pointer rounded-md border-2 overflow-hidden transition-all ${isSel ? 'border-primary' : 'border-muted hover:border-muted-foreground/30'}`}
                    >
                      {o.url ? (
                        <>
                          <img src={o.url} alt={`Photo ${i + 1}`} className="w-full h-56 object-cover" onError={refreshMediaUrls} />
                          <AssetDownloadButton workspaceId={workspaceId} mediaId={mediaId} uid={o.uid} />
                        </>
                      ) : (
                        <div className="w-full h-56 bg-muted/30 flex items-center justify-center text-xs text-muted-foreground p-2 text-center">
                          {o.metadata?.brief || o.metadata?.error || 'No image'}
                        </div>
                      )}
                      <div className="flex items-center justify-between p-2 text-xs">
                        <span className="flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Photo {i + 1}</span>
                        {isSel && <span className="text-primary flex items-center gap-1"><Check className="h-3 w-3" /> In use</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {hasVideos && (
              <div className="space-y-3">
                {gen.videoOptions.map((o, i) => {
                  const isSel = content?.selection.videoUids.includes(o.uid);
                  return (
                    <div
                      key={o.uid}
                      onClick={() => handleToggleVideo(o.uid)}
                      className={`group relative cursor-pointer rounded-md border-2 overflow-hidden transition-all ${isSel ? 'border-primary' : 'border-muted hover:border-muted-foreground/30'}`}
                    >
                      {o.url ? (
                        <>
                          <video
                            src={o.url}
                            className="w-full h-56 object-cover bg-black"
                            muted
                            playsInline
                            preload="metadata"
                            controls
                            onClick={(e) => e.stopPropagation()}
                            onError={refreshMediaUrls}
                          />
                          <AssetDownloadButton workspaceId={workspaceId} mediaId={mediaId} uid={o.uid} />
                        </>
                      ) : (
                        <div className="w-full h-56 bg-muted/30 flex items-center justify-center text-xs text-muted-foreground p-2 text-center">
                          {o.metadata?.error || o.metadata?.angle || 'No video'}
                        </div>
                      )}
                      <div className="flex items-center justify-between p-2 text-xs">
                        <span className="flex items-center gap-1"><Video className="h-3 w-3" /> Video {i + 1}</span>
                        {isSel && <span className="text-primary flex items-center gap-1"><Check className="h-3 w-3" /> In use</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-1 p-4 md:p-8">
        <div className="container mx-auto max-w-5xl space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t('create.title')}</h1>
            <p className="text-muted-foreground">{t('create.subtitle')}</p>
          </div>

          {/* Collapsible campaign details form */}
          <Collapsible open={formOpen} onOpenChange={setFormOpen}>
            <Card className="overflow-hidden">
              <CollapsibleTrigger asChild>
                <button type="button" className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-accent/40 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold">Campaign details</h2>
                      {!formOpen && <p className="text-sm text-muted-foreground truncate">{summaryLine}</p>}
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${formOpen ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-6 border-t p-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Campaign Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Summer Villa Sale"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Campaign Channels</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { value: 'ad', label: 'Social Media Ad' },
                        { value: 'email', label: 'Email Blast' },
                        { value: 'whatsapp', label: 'WhatsApp' },
                        { value: 'sms', label: 'SMS' },
                      ].map((ch) => (
                        <label key={ch.value} className={`flex items-center gap-2 border p-3 rounded-md cursor-pointer hover:bg-accent/50 transition-colors ${platforms.includes(ch.value) ? 'border-primary bg-primary/5' : ''}`}>
                          <Checkbox
                            checked={platforms.includes(ch.value)}
                            onCheckedChange={(c) => handlePlatformToggle(ch.value, c as boolean)}
                          />
                          <span className="text-sm font-medium">{ch.label}</span>
                        </label>
                      ))}
                    </div>
                    {platforms.length === 0 && (
                      <p className="text-xs text-destructive">Select at least one channel</p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>Campaign Type</Label>
                    <RadioGroup
                      value={campaignStage}
                      onValueChange={(v) => setCampaignStage(v as CampaignStage)}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                    >
                      {CAMPAIGN_STAGE_OPTIONS.map((opt) => (
                        <label key={opt.value} className={`flex items-center gap-2 border p-3 rounded-md cursor-pointer hover:bg-accent/50 transition-colors ${campaignStage === opt.value ? 'border-primary bg-primary/5' : ''}`}>
                          <RadioGroupItem value={opt.value} />
                          <span className="text-sm font-medium">{opt.label}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="unit-type">Unit Type *</Label>
                      <Input
                        id="unit-type"
                        placeholder="e.g., 2BR Apartment"
                        value={unitType}
                        onChange={(e) => setUnitType(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location *</Label>
                      <Input
                        id="location"
                        placeholder="e.g., Dubai Marina"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="developer">Developer</Label>
                      <Input
                        id="developer"
                        placeholder="e.g., Emaar"
                        value={developer}
                        onChange={(e) => setDeveloper(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pricing">Pricing</Label>
                      <Input
                        id="pricing"
                        placeholder="e.g., AED 1.5M"
                        value={pricing}
                        onChange={(e) => setPricing(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="range">Size Range</Label>
                      <Input
                        id="range"
                        placeholder="e.g., 1200-1500 sqft"
                        value={propertyRange}
                        onChange={(e) => setPropertyRange(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="brief">Additional Brief / Key Features</Label>
                    <Textarea
                      id="brief"
                      placeholder="e.g., Sea view, payment plan 50/50, handover Q4 2025"
                      value={brief}
                      onChange={(e) => setBrief(e.target.value)}
                      rows={3}
                    />
                    {genCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Tip: tweak the brief or details, then generate another option to compare.
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>Reference Images (Optional)</Label>
                    <p className="text-sm text-muted-foreground">
                      Upload property photos to help AI generate more accurate content
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {referenceImages.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img.preview}
                            alt={`Reference ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-md border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {referenceImages.length < 5 && (
                        <label className="w-20 h-20 border-2 border-dashed rounded-md flex flex-col items-center justify-center cursor-pointer hover:bg-accent/50 transition-colors">
                          <Upload className="h-5 w-5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground mt-1">Add</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                    {referenceImages.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {referenceImages.length}/5 images uploaded
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label>Content Required</Label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2 border p-3 rounded-md flex-1 min-w-[120px] justify-center cursor-pointer hover:bg-accent/50">
                        <Checkbox
                          id="type-text"
                          checked={contentTypes.includes('text')}
                          onCheckedChange={(c) => handleContentTypeChange('text', c as boolean)}
                        />
                        <Label htmlFor="type-text" className="cursor-pointer flex items-center gap-2">
                          <FileText className="h-4 w-4" /> Text
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 border p-3 rounded-md flex-1 min-w-[120px] justify-center cursor-pointer hover:bg-accent/50">
                        <Checkbox
                          id="type-photo"
                          checked={contentTypes.includes('photo')}
                          onCheckedChange={(c) => handleContentTypeChange('photo', c as boolean)}
                        />
                        <Label htmlFor="type-photo" className="cursor-pointer flex items-center gap-2">
                          <ImageIcon className="h-4 w-4" /> Photo
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 border p-3 rounded-md flex-1 min-w-[120px] justify-center cursor-pointer hover:bg-accent/50">
                        <Checkbox
                          id="type-video"
                          checked={contentTypes.includes('video')}
                          onCheckedChange={(c) => handleContentTypeChange('video', c as boolean)}
                        />
                        <Label htmlFor="type-video" className="cursor-pointer flex items-center gap-2">
                          <Video className="h-4 w-4" /> Video
                        </Label>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Generation cost: {totalCost} credits{isSuperAdmin ? ' (no charge for super admins)' : ''}
                    </p>
                  </div>

                  <Button
                    onClick={guard(requestGenerate)}
                    disabled={loading}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Generating…
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-5 w-5" />
                        {genCount > 0 ? 'Generate another option' : 'Generate Campaign'}
                      </>
                    )}
                  </Button>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Main area: loader while generating, else review, else empty state */}
          {loading ? (
            <Card className="min-h-[420px] flex items-center justify-center">
              <GenerationLoader isRegenerate={genCount > 0} />
            </Card>
          ) : content && genCount > 0 ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Your options</h2>
                  <Badge variant="secondary">{genCount} option{genCount > 1 ? 's' : ''}</Badge>
                </div>
                <Button variant="outline" size="sm" onClick={guard(requestGenerate)} disabled={loading}>
                  <Sparkles className="mr-2 h-4 w-4" /> Generate another option
                </Button>
              </div>

              <p className="text-sm text-muted-foreground">
                Compare the options and pick the text, photo(s) and video(s) you want — you can mix across options.
              </p>
              <div className="space-y-4">
                {content.generations.map((gen) => renderOptionCard(gen))}
              </div>

              {/* Final selection */}
              <Card className="p-6 border-primary/30 space-y-4">
                <Label className="font-semibold text-base">Final selection</Label>

                {/* Text row (full width) */}
                {selText ? (
                  <div className="rounded-md border border-primary/40 bg-primary/5 p-4">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3 w-3" /> Text · {selText.genLabel}
                    </span>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed mt-1">{selText.content}</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No text selected</p>
                )}

                {/* Media row: photos + videos (half/half when both, full width when one) */}
                {(selPhotos.length > 0 || selVideos.length > 0) && (
                  <div className={`grid gap-4 ${selPhotos.length > 0 && selVideos.length > 0 ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                    {selPhotos.length > 0 && (
                      <div className="space-y-3">
                        {selPhotos.map((p) => p.url && (
                          <div key={p.uid} className="group relative rounded-md border overflow-hidden">
                            <img src={p.url} alt="Selected" className="w-full h-56 object-cover" onError={refreshMediaUrls} />
                            <AssetDownloadButton workspaceId={workspaceId} mediaId={mediaId} uid={p.uid} />
                            <div className="flex items-center gap-1 p-2 text-xs text-muted-foreground">
                              <ImageIcon className="h-3 w-3" /> Photo · {p.genLabel}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {selVideos.length > 0 && (
                      <div className="space-y-3">
                        {selVideos.map((v) => v.url && (
                          <div key={v.uid} className="group relative rounded-md border overflow-hidden">
                            <video src={v.url} className="w-full h-56 object-cover bg-black" muted playsInline preload="metadata" controls onError={refreshMediaUrls} />
                            <AssetDownloadButton workspaceId={workspaceId} mediaId={mediaId} uid={v.uid} />
                            <div className="flex items-center gap-1 p-2 text-xs text-muted-foreground">
                              <Video className="h-3 w-3" /> Video · {v.genLabel}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  For WhatsApp, the selected text becomes a template&rsquo;s message and the selected
                  photo or video its header — and campaigns using that template send that picture.
                  SMS sends the text only.
                </p>
              </Card>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={guard(handleSave)} disabled={saving} className="flex-1" size="lg">
                  {saving ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Saving...</> : 'Save media'}
                </Button>
                {handOff === 'template' && (
                  <Button
                    onClick={guard(handleUseInTemplate)}
                    disabled={starting || !hasSelection}
                    className="flex-1"
                    size="lg"
                    variant="outline"
                  >
                    {starting
                      ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Opening…</>
                      : <><MessageSquare className="mr-2 h-5 w-5" /> Use in a template</>}
                  </Button>
                )}
                {handOff === 'campaign' && (
                  <Button
                    onClick={guard(handleUseInCampaign)}
                    disabled={starting}
                    className="flex-1"
                    size="lg"
                    variant="outline"
                  >
                    {starting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Starting…</> : 'Use in a campaign'}
                  </Button>
                )}
              </div>
              {handOff === 'template' && !hasSelection && (
                <p className="text-xs text-muted-foreground">
                  Select the text, photo or video above to use them in a template.
                </p>
              )}

              {/* Where this creative went next. Shown once a template exists, so the page that
                  started the flow also says how far it got. */}
              {linkedTemplates.length > 0 && (
                <Card className="p-5 space-y-3">
                  <Label className="font-semibold">Templates made from this media</Label>
                  <ul className="space-y-2">
                    {linkedTemplates.map((tpl) => (
                      <li key={tpl.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="font-medium">{formatTemplateName(tpl.name)}</span>
                        {tpl.status === 'APPROVED' && canCreateCampaign ? (
                          <Button size="sm" onClick={guard(() => navigate(campaignFromTemplatePath(workspaceId, tpl.id)))}>
                            <Megaphone className="mr-1.5 h-3.5 w-3.5" /> Create campaign
                          </Button>
                        ) : tpl.status === 'APPROVED' ? (
                          <Badge variant="outline" className="border-green-300 text-green-700">Approved</Badge>
                        ) : tpl.status === 'PENDING' ? (
                          <Badge variant="outline" className="border-yellow-300 text-yellow-700">In review</Badge>
                        ) : tpl.status === 'REJECTED' ? (
                          <span className="text-xs text-red-600">
                            Rejected{tpl.rejectionReason ? `: ${tpl.rejectionReason}` : ''}
                          </span>
                        ) : (
                          <Badge variant="outline">{tpl.status}</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    WhatsApp reviews new templates, usually within a few minutes to a few hours. An
                    approved one starts a campaign with this media already chosen.{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => navigate(`/w/${workspaceId}/whatsapp-templates`)}
                    >
                      Check for updates
                    </button>
                  </p>
                </Card>
              )}
            </div>
          ) : (
            <Card className="min-h-[320px] flex flex-col items-center justify-center text-center text-muted-foreground p-8">
              <div className="bg-muted/30 p-6 rounded-full mb-4">
                <Sparkles className="h-12 w-12 opacity-50" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-foreground">Ready to create?</h3>
              <p className="max-w-xs">
                Fill in the details above and let our AI generate professional content for your campaign.
              </p>
              {!formOpen && (
                <Button className="mt-4" variant="outline" onClick={() => setFormOpen(true)}>
                  Open the form
                </Button>
              )}
            </Card>
          )}
        </div>
        {nudge}
      </main>

      <AlertDialog open={showGenConfirm} onOpenChange={setShowGenConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{genCount > 0 ? 'Generate another option?' : 'Generate campaign content?'}</AlertDialogTitle>
            <AlertDialogDescription>
              This runs the AI generation for the selected content types and uses credits. Generation can take a few minutes — keep this page open.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 text-sm">
            <ul className="list-disc pl-5">
              {costBreakdown(contentTypes, creditCosts.generation).map((b) => (
                <li key={b.type} className="capitalize">{b.type}: {b.cost} credits</li>
              ))}
            </ul>
            <p className="font-medium">Total: {totalCost} credits{isSuperAdmin ? ' (no charge for super admins)' : ''}</p>
            {!isSuperAdmin && (
              <p className="text-muted-foreground">Remaining: {formatCredits(workspace.creditsRemaining)} credits</p>
            )}
            {cannotAfford && (
              <p className="text-destructive">
                Not enough credits for this generation.{' '}
                <button type="button" className="underline" onClick={() => navigate(`/w/${workspaceId}/billing`)}>
                  Buy top-up credits
                </button>
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runGeneration} disabled={cannotAfford || loading}>Generate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
